import { ConnectAccountManagement } from "@stripe/react-connect-js";
import { createFileRoute } from "@tanstack/react-router";
import { ShieldAlert } from "lucide-react";

import { PageWrapper } from "~/components/page-wrapper";
import { ConnectProvider } from "~/components/stripe/connect-provider";
import { Alert, AlertDescription, AlertTitle } from "~/components/ui/alert";
import { Skeleton } from "~/components/ui/skeleton";
import { useEmployeePermissions } from "~/hooks/use-employee-permissions";
import { useActiveOrganization } from "~/integrations/vortex-auth/hooks";

export const Route = createFileRoute(
  "/_authenticated/settings/payment-account"
)({
  component: PaymentAccountPage,
  pendingComponent: () => (
    <PageWrapper
      description="Manage your bank accounts and payout settings"
      title="Payment Account"
    >
      <div className="bg-card animate-pulse rounded-xl border">
        <div className="space-y-6 p-6">
          <div className="bg-muted h-6 w-40 rounded-md" />
          <div className="space-y-4">
            <div className="border-border flex items-center justify-between rounded-lg border p-4">
              <div className="flex items-center gap-3">
                <div className="bg-muted h-10 w-10 rounded" />
                <div className="space-y-2">
                  <div className="bg-muted h-4 w-32 rounded" />
                  <div className="bg-muted h-3 w-24 rounded" />
                </div>
              </div>
              <div className="bg-muted h-8 w-20 rounded" />
            </div>
          </div>
          <div className="space-y-4">
            <div className="bg-muted h-4 w-36 rounded" />
            <div className="grid gap-4 md:grid-cols-2">
              <div className="bg-muted h-10 rounded-md" />
              <div className="bg-muted h-10 rounded-md" />
            </div>
          </div>
        </div>
      </div>
    </PageWrapper>
  ),
});

function PaymentAccountPage() {
  const { organization } = useActiveOrganization();
  const { hasPermission, isLoading, hasEmployee } = useEmployeePermissions();
  const canViewIntegrations = hasPermission("viewIntegrations");

  if (!organization) {
    return null;
  }

  // Loading state
  if (isLoading) {
    return (
      <PageWrapper
        description="Manage your bank accounts and payout settings"
        title="Payment Account"
      >
        <Skeleton className="h-96 w-full" />
      </PageWrapper>
    );
  }

  // No employee record linked
  if (!hasEmployee) {
    return (
      <PageWrapper
        description="Manage your bank accounts and payout settings"
        title="Payment Account"
      >
        <Alert>
          <ShieldAlert className="h-4 w-4" />
          <AlertTitle>No Employee Profile</AlertTitle>
          <AlertDescription>
            Your account is not linked to an employee profile. Contact your
            organization admin to set up your employee record with the
            appropriate permissions.
          </AlertDescription>
        </Alert>
      </PageWrapper>
    );
  }

  // No permission to view payment account
  if (!canViewIntegrations) {
    return (
      <PageWrapper
        description="Manage your bank accounts and payout settings"
        title="Payment Account"
      >
        <Alert variant="destructive">
          <ShieldAlert className="h-4 w-4" />
          <AlertTitle>Access Denied</AlertTitle>
          <AlertDescription>
            You don't have permission to view payment account settings. Contact
            your manager to request access.
          </AlertDescription>
        </Alert>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper
      description="Manage your bank accounts and payout settings"
      title="Payment Account"
    >
      <ConnectProvider
        className="bg-card overflow-hidden rounded-xl border p-6"
        organizationId={organization.id}
      >
        <ConnectAccountManagement />
      </ConnectProvider>
    </PageWrapper>
  );
}
